import { Component, ViewContainerRef, OnInit, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take, takeUntil } from 'rxjs/operators';
import { SpinnerComponent } from "./shared/component/spinnercomponent/spinner.component";
import { HttpClient } from '@angular/common/http';
import { UpdateService } from 'services/update.service';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { NavigationService } from 'services/navigation.service';
import { AuthenticationService } from 'services/authentication.service';
import { AlertService } from 'services/alert.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly _destroying$ = new Subject<void>();
    spinnerComponent = SpinnerComponent;
    private isLoginInProgress = false;
    subscription: Subscription | undefined;

    constructor(private router: Router, private http: HttpClient,
        private sw: UpdateService,
        private msalBroadcastService: MsalBroadcastService,
        private navigate: NavigationService,
        private msalService: MsalService,
        private authService: AuthenticationService,
        private alertService: AlertService,

    ) {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                take(1)
            )
            .subscribe(() => {
                const currentUrl = this.router.url;
                const token = localStorage.getItem('token');
                if (!currentUrl.endsWith('/login') && !currentUrl.includes('/login-callback')) {
                    return;
                }
                // ✅ Already logged in
                if (token && !this.isTokenExpired(token)) {
                    this.router.navigate(['/dashboard']);
                    return;
                }

                // ✅ Use inProgress$ + handleRedirectObservable — never misses redirect result
                this.msalBroadcastService.inProgress$
                    .pipe(
                        filter((status: InteractionStatus) => status === InteractionStatus.None),
                        take(1),
                        takeUntil(this._destroying$)
                    )
                    .subscribe(() => {
                        this.msalService.handleRedirectObservable()
                            .pipe(takeUntil(this._destroying$))
                            .subscribe({
                                next: (result: AuthenticationResult | null) => {
                                    if (result) {
                                        // ✅ Fresh redirect — token available directly
                                        this.processLogin(result);
                                    } else {
                                        // ✅ Page refresh — get token silently
                                        const account = this.msalService.instance.getActiveAccount()
                                            || (this.msalService.instance.getAllAccounts() || [])[0]
                                            || null;

                                        if (!account) {
                                            this.clearMsalAndAppStorage()
                                            return;
                                        }

                                        this.msalService.instance.acquireTokenSilent({
                                            scopes: ['openid', 'profile', 'email'],
                                            account
                                        }).then(silentResult => {
                                            this.processLogin(silentResult);
                                        }).catch(() => {
                                            this.clearMsalAndAppStorage()
                                        });
                                    }
                                },
                                error: () => this.clearMsalAndAppStorage()
                            });
                    });
            });



        // check the service worker for updates
        this.sw.checkForUpdates();
    }

    ngOnInit() {
        this.subscription = this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe(() => window.scrollTo(0, 0));
    }


    processLogin(result: AuthenticationResult): void {
        const claims: any = result.idTokenClaims;
        const email = claims.preferred_username || claims.email || claims.upn;

        if (!email) {
            console.error('Email not found in claims:', claims);
            this.clearMsalAndAppStorage()
            return;
        }

        // ✅ setActiveAccount before anything else
        this.msalService.instance.setActiveAccount(result.account);

        this.authService.login(email, '', null, false, false, result.idToken)
            .subscribe({
                next: (res) => {
                    if (!res.isSuccess) {
                        // ✅ Handle failed login
                        this.alertService.error(res.htmlFormattedFailures);
                        this.clearMsalAndAppStorage()
                        return;
                    }
                    // ✅ Navigate only after everything is set
                    this.navigate.goToDashboard();
                },
                error: (err) => {
                    console.error('Login API failed:', err);
                    this.clearMsalAndAppStorage()
                }
            });
    }


    private isTokenExpired(token: string): boolean {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const valid: any = Math.floor(Date.now() / 1000) >= payload.exp;
            console.log("token expiered or not" + valid);

            return valid;
        } catch (e) {
            return true;
        }
    }
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    onLogin(data: any) {
        debugger
        this.authService.login(data.email, '', null, false, false, data.claims.toString()).subscribe((result) => {
            if (!result.isSuccess) {
                this.alertService.error(result.htmlFormattedFailures);
                return
            } else {
                this.navigate.goToDashboard();
            }
        });

    }


    clearMsalAndAppStorage(): void {
        this.msalService.instance.setActiveAccount(null);

        Object.keys(localStorage)
            .filter(key =>
                key.startsWith('msal.') ||
                key.includes('login.windows.net') ||
                key.includes('login.microsoftonline')
            )
            .forEach(key => localStorage.removeItem(key));

        Object.keys(sessionStorage)
            .filter(key =>
                key.startsWith('msal.') ||
                key.includes('login.windows.net') ||
                key.includes('login.microsoftonline')
            )
            .forEach(key => sessionStorage.removeItem(key));

        localStorage.removeItem('isLoggedin');
        localStorage.removeItem('token');

        this.router.navigate(['/login']);
    }
}
